import React, { memo, useEffect, useState, useRef } from "react";
import useFetch from "use-http";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PageQuery, { getQueryStringFromPageQuery } from "common/types/PageQuery";
import { PagedResponse } from "common/types/PagedResponse";
import { useToaster } from "contexts/Toaster";
import Context from "./context";
import { SceneObject, Material, Light, Combination } from "./types";
import useOrganizations from "../Organizations/useOrganizations";
import { useAuth, isB2cUser } from "contexts/Auth";
import { SGVSB_DEFAULT_CATEGORY } from "common/constants/sgvsb";
import { isBmsOrganizationId } from "hooks/useIsB2cContext";
import type { CombinationClassificationFilter } from "utils/combinationClassification";
import type { LibraryVisibility } from "components/VisibilitySelect/VisibilitySelect";
import { getCombinationDisplayName } from "utils/combinationDisplayName";
import { BMS_ORG_ID } from "views/SharedScene/StreamingStatesConstants";
import { getMaterialsBrowseLevel, getObjectsBrowseLevel } from "./objectsBrowseLevel";

/**
 * Read the page of results out of a paged list response.
 *
 * A paged endpoint always answers with `{ results: [...] }`. When it doesn't —
 * an error body, or use-http resolving `undefined` on a transport failure while
 * `response.ok` still reflects the PREVIOUS request — the raw value used to be
 * written straight into state. `objects`/`materials`/`lights` would become
 * `undefined`, and the next `.map()` in the Library tab threw, taking the whole
 * tab down through its error boundary. An empty page is the only safe reading
 * of "no results came back".
 */
/**
 * Combinations in the order the gallery should show them: by the name the user
 * actually reads, in the language they are reading it in.
 *
 * Sorted here rather than by the server because the server cannot do it. Its
 * list endpoint sorts on meta.nameEN, which is the authoring locale, not the
 * displayed one — a combination named "Waschtisch" in German and "Vanity unit"
 * in English sits under V for a German user. `getCombinationDisplayName` is the
 * same resolution the tiles use, so the order always matches the labels.
 *
 * localeCompare with `numeric` so "Set 2" precedes "Set 10", and
 * `sensitivity: "base"` so case and accents do not split otherwise adjacent
 * names apart.
 */
const sortCombinationsByName = (
	list: Combination[],
	language: string,
): Combination[] =>
	[...list].sort((a, b) =>
		(getCombinationDisplayName(a, language) ?? "").localeCompare(
			getCombinationDisplayName(b, language) ?? "",
			language,
			{ numeric: true, sensitivity: "base" },
		),
	);

const pageResults = (response: any): any[] =>
	Array.isArray(response?.results) ? response.results : [];

export const initialMaterialsQuery: PageQuery = {
	filters: {
		// Public only by default, and never both at once - same reason as objects
		// above: public+private in one request is a top-level $or that no index can
		// serve while also supplying the sort, so it walks the whole collection.
		// The visibility filter in FilterAndSortMaterials enforces the either/or.
		isPublic: true,
		isPrivate: false,
		category: [],
		tag: [],
		// Tiles only — see contexts/Library/materialApplicationFilter.
		application: [],
		indoorOutdoor: [],
	},
	limit: 24,
	page: 1,
	search: {
		text: "",
		fields: [],
	},
	sort: "meta.name",
};

export const getInitialObjectsQuery = (organizationId?: string): PageQuery => {
	// Only pre-select bathroom category for specific organization
	const shouldPreselectBathroom = organizationId === BMS_ORG_ID;
	const category = shouldPreselectBathroom ? [SGVSB_DEFAULT_CATEGORY] : [];

	return {
		filters: {
			// Public only by default, and never both at once. Public+private in one
			// request is a top-level $or that no index can serve while also supplying
			// the sort, so it walked all 523k objects per library page load and
			// OOM-killed mongod repeatedly through 2026. Only 792 of 163,722
			// non-trash objects are private. See docs/mongo-objects-full-scan.md.
			isPublic: true,
			isPrivate: false,
			category,
			tag: [],
			// sgvsbOnly is deliberately absent: the filter always starts off and is
			// only ever turned on by the user clicking its checkbox. It used to be
			// derived from the bathroom category, which decided for them.
		},
		limit: 24,
		page: 1,
		search: {
			text: "",
			fields: [],
		},
		sort: "meta.name",
	};
};

export const initialObjectsQuery: PageQuery = getInitialObjectsQuery();

/**
 * The combination lists start where the objects do: the BMS org on Bad
 * (getInitialObjectsQuery), everyone else on everything. Matched across
 * environments so stage and the test org behave like production.
 */
export const getInitialCombinationClassificationFilter = (
	organizationId?: string,
): CombinationClassificationFilter => ({
	category: isBmsOrganizationId(organizationId) ? SGVSB_DEFAULT_CATEGORY : null,
	subcategory: null,
});

export const initialLightsQuery: PageQuery = {
	filters: {
		isPrivate: true,
		isPublic: true,
		category: [],
		tag: [],
	},
	limit: 24,
	page: 1,
	search: {
		text: "",
		fields: [],
	},
	sort: "meta.name",
};

export const initialCombinationsQuery: PageQuery = {
	filters: {
		isPrivate: true,
		isPublic: true,
		category: [],
		tag: [],
	},
	/* One page, deliberately. The combinations endpoint cannot search — its
	   getAll builds the match from isTrash and the access conditions only and
	   never reads search[text] — so the library filters the list in the browser
	   instead, and a filter can only be honest about what has been loaded.
	   A few hundred curated combinations is the whole collection. */
	limit: 192,
	page: 1,
	search: {
		text: "",
		fields: [],
	},
	sort: "meta.name",
};

interface LibraryProviderProps {
	children?: React.ReactNode;
	/**
	 * Leave the object and material libraries unfetched until the query
	 * narrows them, offering category (and subcategory) cards meanwhile — the
	 * scene editor's library tab. See ./objectsBrowseLevel.
	 */
	browseByCategory?: boolean;
}


/**
 * `items` with every repeated `_id` dropped, first occurrence kept, order
 * otherwise unchanged.
 *
 * The library lists grow page by page, and the server's name sort has no
 * unique tiebreaker for objects (a second sort key the name index cannot serve
 * would turn every page into an in-memory sort over the whole collection). With
 * several records sharing a name, skip/limit may then hand the same record out
 * on two pages. Appended as-is, that put two cards with one React key into the
 * gallery — "Encountered two children with the same key", many times over,
 * and cards duplicated or dropped on update. Deduplicated at the one place
 * every page lands.
 */
function uniqueById<T extends { _id?: string }>(items: T[]): T[] {
	const seen = new Set<string>();
	return items.filter((item) => {
		const id = item?._id;
		if (!id) return true;
		if (seen.has(id)) return false;
		seen.add(id);
		return true;
	});
}

const LibraryProvider: React.FC<LibraryProviderProps> = memo((props) => {
	const { children, browseByCategory = false } = props;
	const { current: currentOrganization } = useOrganizations();
	const { shortid: shortidParam, kind: kindParam } = useParams<{
		shortid: string;
		kind: string;
	}>();
	const { t, i18n } = useTranslation();
	const history = useHistory();
	const { search, pathname } = useLocation();
	const toaster = useToaster();

	// Check if we're on the project scenes gallery page (doesn't need objects)
	// Pattern: /projects/:projectId/scenes (but NOT /projects/:projectId/scenes/:sceneId)
	const isProjectScenesGallery = /\/projects\/[^/]+\/scenes$/.test(pathname);

	// End-consumer accounts build from combinations and a curated set of
	// materials — no objects, no light sources. The server enforces this; here
	// it only saves the roundtrips and lets the library hide the empty tabs.
	const { me } = useAuth();
	const isB2c = isB2cUser(me);


	const [objects, setObjects] = useState<SceneObject[]>([]);
	const [objectsCount, setObjectsCount] = useState<number>(-1);
	const [currentObjectState, setCurrentObjectState] = useState<SceneObject>();
	const [objectsQuery, setObjectsQuery] = useState<PageQuery>(() =>
		getInitialObjectsQuery(currentOrganization?._id)
	);
	const appendObjects = useRef<boolean>(false);
	const [pagedObjectsResponse, setPagedObjectsResponse] = useState<
		PagedResponse<SceneObject>
	>();

	// Grouped objects state for variation stacking
	const [groupedObjects, setGroupedObjects] = useState<any[]>([]);
	const [pagedGroupedObjectsResponse, setPagedGroupedObjectsResponse] = useState<any>();
	const [loadingGroupedObjects, setLoadingGroupedObjects] = useState<boolean>(false);
	const appendGroupedObjects = useRef<boolean>(false);
	const hasPerformedInitialLoad = useRef<boolean>(false);

	// All objects cache - maintains full list regardless of search/filters
	// Used by floorplan and other features that need access to all objects
	const [allObjectsCache, setAllObjectsCache] = useState<SceneObject[]>([]);

	// Race condition prevention: Request ID tracking for objects
	const objectsRequestIdRef = useRef<number>(0);

	const [materials, setMaterials] = useState<Material[]>([]);
	const [currentMaterialState, setCurrentMaterialState] = useState<Material>();
	const [materialsQuery, setMaterialsQuery] = useState<PageQuery>(
		initialMaterialsQuery,
	);
	const appendMaterials = useRef<boolean>(false);
	const [pagedMaterialsResponse, setPagedMaterialsResponse] = useState<
		PagedResponse<Material>
	>();

	// Race condition prevention: Request ID tracking for materials
	const materialsRequestIdRef = useRef<number>(0);

	const [lights, setLights] = useState<Light[]>([]);
	const [currentLightState, setCurrentLightState] = useState<Light>();
	const [lightsQuery, setLightsQuery] = useState<PageQuery>(
		initialLightsQuery,
	);
	const appendLights = useRef<boolean>(false);
	const [pagedLightsResponse, setPagedLightsResponse] = useState<
		PagedResponse<Light>
	>();

	// Race condition prevention: Request ID tracking for lights
	const lightsRequestIdRef = useRef<number>(0);

	const [combinations, setCombinations] = useState<Combination[]>([]);
	const [currentCombinationState, setCurrentCombinationState] = useState<Combination>();
	const [combinationsQuery, setCombinationsQuery] = useState<PageQuery>(initialCombinationsQuery);
	const [combinationClassificationFilter, setCombinationClassificationFilter] =
		useState<CombinationClassificationFilter>(() =>
			getInitialCombinationClassificationFilter(currentOrganization?._id),
		);
	// Public by default, like the objects and materials.
	const [combinationVisibility, setCombinationVisibility] =
		useState<LibraryVisibility>("isPublic");
	const appendCombinations = useRef<boolean>(false);
	const [pagedCombinationsResponse, setPagedCombinationsResponse] = useState<PagedResponse<Combination>>();
	const combinationsRequestIdRef = useRef<number>(0);

	// These are fetched once & passed down to AFrame
	// const [allObjects, setAllObjects] = useState<SceneObject[]>([]);
	const [allMaterials, setAllMaterials] = useState<Material[]>([]);
	const [allLights, setAllLights] = useState<Light[]>([]);

	const isLibraryView = window.location.pathname.includes("library");

	const objectsBrowseLevel = browseByCategory
		? getObjectsBrowseLevel(objectsQuery)
		: null;
	const materialsBrowseLevel = browseByCategory
		? getMaterialsBrowseLevel(materialsQuery)
		: null;

	const {
		get: getObjects,
		loading: loadingObjects,
		error: errorObjects,
		response: responseObjects,
	} = useFetch("objects");

	const {
		get: getMaterials,
		loading: loadingMaterials,
		error: errorMaterials,
		response: responseMaterials,
	} = useFetch("materials");

	const {
		get: getLights,
		loading: loadingLights,
		error: errorLights,
		response: responseLights,
	} = useFetch("lights");

	const {
		get: getGroupedObjects,
		response: responseGroupedObjects
	} = useFetch("objects/grouped");

	const {
		get: getCombinations,
		loading: loadingCombinations,
		error: errorCombinations,
		response: responseCombinations,
	} = useFetch("combinations");

/*
	const { get: getAllObjects, response: responseAllObjects } = useFetch(
		"objects", {
			limit: 11000
		}
	);
*/
	const { get: getAllMaterials, response: responseAllMaterials } = useFetch(
		"materials"
	);
	const { get: getAllLights, response: responseAllLights } = useFetch(
		"lights",
	);


	const fetchObjects = async () => {
		const queryString = getQueryStringFromPageQuery(objectsQuery);
		const response = await getObjects(`?${queryString}`);

		if (responseObjects.ok) {
			const results = uniqueById(appendObjects.current
				? [...objects, ...pageResults(response)]
				: pageResults(response));

			setObjects(results);
			setObjectsCount(response.totalResults !== undefined ? response.totalResults : -1);

			// TODO refactor the communication with hegias-3d in a way
			// that we dynamically update their list of objects like the following:
			// console.log("Setting allObjects=", [...allObjects, ...results]);
			// setAllObjects([...allObjects, ...results]);
			// The prop seems to update correctly, but hegias-3d doesn't seem to update the list internally then

			setPagedObjectsResponse(response);
			appendObjects.current = false;

			// console.log("fetched objects", results);
		}
	};

	const fetchGroupedObjects = async () => {
		// Track this request with incremental ID to prevent race conditions
		const requestId = ++objectsRequestIdRef.current;

		// Snapshot and reset the append flag synchronously (before the request goes async).
		// If we read appendGroupedObjects.current only after the await, a stale/discarded
		// request (see the requestId check below) can leave it stuck at `true`, causing a
		// later, unrelated request's results to be appended to old state instead of
		// replacing it.
		const shouldAppend = appendGroupedObjects.current;
		appendGroupedObjects.current = false;

		setLoadingGroupedObjects(true);

		// Check if there's a search term or any filters selected
		const hasSearchText = objectsQuery.search.text && objectsQuery.search.text.trim() !== '';
		const hasCategories = objectsQuery.filters?.category && objectsQuery.filters.category.length > 0;
		const hasTags = objectsQuery.filters?.tag && objectsQuery.filters.tag.length > 0;
		const hasSubCategory1 = objectsQuery.filters?.subCategory1 && objectsQuery.filters.subCategory1.length > 0;
		const hasSubCategory2 = objectsQuery.filters?.subCategory2 && objectsQuery.filters.subCategory2.length > 0;
		const hasSubCategory3f = objectsQuery.filters?.subCategory3f && objectsQuery.filters.subCategory3f.length > 0;
		const hasManufacturers = objectsQuery.filters?.manufacturers && objectsQuery.filters.manufacturers.length > 0;
		const hasColors = objectsQuery.filters?.colors && objectsQuery.filters.colors.length > 0;
		const hasDimensions = objectsQuery.filters?.dimensions && objectsQuery.filters.dimensions.length > 0;
		const hasExecutions = objectsQuery.filters?.executions && objectsQuery.filters.executions.length > 0;
		// Include visibility filters in criteria check
		const hasVisibilityFilter = objectsQuery.filters?.isPrivate !== undefined || objectsQuery.filters?.isPublic !== undefined;
		const hasSgvsbOnly = objectsQuery.filters?.sgvsbOnly === true;

		const hasAnyCriteria = hasSearchText || hasCategories || hasTags || hasSubCategory1 || hasSubCategory2 ||
			hasSubCategory3f || hasManufacturers || hasColors || hasDimensions || hasExecutions || hasVisibilityFilter ||
			hasSgvsbOnly;

		// If no search criteria, show empty results in library UI
		// BUT: Allow initial load to proceed (will fetch with whatever initial query state exists)
		if (!hasAnyCriteria && hasPerformedInitialLoad.current) {
			setGroupedObjects([]);
			setObjects([]);
			setObjectsCount(0);
			setPagedGroupedObjectsResponse(undefined);
			setLoadingGroupedObjects(false);
			return;
		}

		try {
			const queryString = getQueryStringFromPageQuery(objectsQuery);
			const response = await getGroupedObjects(`?${queryString}`);

			// Only process if this is still the latest request
			if (requestId !== objectsRequestIdRef.current) {
				// console.log(`[LibraryProvider] Ignoring stale objects request #${requestId} (current: #${objectsRequestIdRef.current})`);
				return;
			}

			if (responseGroupedObjects.ok) {
			const results = uniqueById(shouldAppend
				? [...groupedObjects, ...pageResults(response)]
				: pageResults(response));

			setGroupedObjects(results);
			setPagedGroupedObjectsResponse(response);

			// Flatten grouped objects into objects array for library UI
			const flattenedObjects: SceneObject[] = [];
			results.forEach((item: any) => {
				if (item.type === 'variant_group') {
					// Add all variations from the group
					flattenedObjects.push(...item.variations);
				} else {
					// Add single object
					flattenedObjects.push(item);
				}
			});

			const searchResults = uniqueById(shouldAppend
				? [...objects, ...flattenedObjects]
				: flattenedObjects);

			// Update search results for library UI
			setObjects(searchResults);
			setObjectsCount(response.totalResults !== undefined ? response.totalResults : -1);

			// Merge new objects into allObjectsCache (deduplicated by shortid)
			setAllObjectsCache(prevCache => {
				const cacheMap = new Map(prevCache.map(obj => [obj.shortid, obj]));
				let newCount = 0;
				flattenedObjects.forEach(obj => {
					if (!cacheMap.has(obj.shortid)) {
						cacheMap.set(obj.shortid, obj);
						newCount++;
					}
				});
				const newCache = Array.from(cacheMap.values());
				// console.log(`[LibraryProvider] allObjectsCache updated: ${prevCache.length} → ${newCache.length} (+${newCount} new)`);
				return newCache;
			});

			// Mark that initial load has been performed
			if (!hasPerformedInitialLoad.current) {
				hasPerformedInitialLoad.current = true;
				// console.log('[LibraryProvider] Initial load completed');
			}
		}

		setLoadingGroupedObjects(false);
		} catch (error: any) {
			// Let errors bubble up to the error handling mechanism
			setLoadingGroupedObjects(false);
			throw error;
		}
	};

	/**
	 * Loads a single object by its ID and adds it to the allObjectsCache.
	 * This is used to preload scene objects for the 3D editor without affecting search results.
	 *
	 * @param {string | null | undefined} idOrNull - The ID of the object to load. If null or undefined, the function will return early.
	 * @returns {Promise<void>} A promise that resolves when the operation is complete.
	 *
	 * @remarks
	 * - Objects are added ONLY to allObjectsCache, NOT to the objects list (which contains search results)
	 * - This separation ensures preloaded scene objects don't pollute library search/filter results
	 * - The 3D editor uses allObjectsCache to access detailed object information
	 *
	 * @example
	 * ```typescript
	 * await loadSingleObject("igu75sdecED");
	 * ```
	 */
	const loadSingleObject = async (idOrNull?: string | null) => {
		if (!idOrNull) {
			return;
		}

		// Check if object already exists in cache
		if (allObjectsCache.find((obj) => obj.shortid === idOrNull)) {
			return;
		}

		const response = await getObjects(`/${idOrNull}/shortid`);

		if (responseObjects.ok) {
			const targetObject = response;

			if (targetObject) {
				// Add to allObjectsCache ONLY (not to objects which contains search results)
				if (!allObjectsCache.find((obj) => obj.shortid === targetObject.shortid)) {
					setAllObjectsCache((prevCache) => {
						// console.log(`[LibraryProvider] loadSingleObject: Adding ${targetObject.shortid} to cache (${prevCache.length} → ${prevCache.length + 1})`);
						return [...prevCache, targetObject];
					});
				} else {
					// console.log(`[LibraryProvider] loadSingleObject: ${targetObject.shortid} already in cache`);
				}
			}

		}
	};

	const fetchMaterials = async () => {
		// Track this request with incremental ID to prevent race conditions
		const requestId = ++materialsRequestIdRef.current;

		try {
			const queryString = getQueryStringFromPageQuery(materialsQuery);
			const response = await getMaterials(`?${queryString}`);
			logger.debug("[LibraryProvider] fetchMaterials", { queryString, requestId });

			// Only process if this is still the latest request
			if (requestId !== materialsRequestIdRef.current) {
				console.log(`[LibraryProvider] Ignoring stale materials request #${requestId} (current: #${materialsRequestIdRef.current})`);
				return;
			}

			if (responseMaterials.ok) {
				const results = uniqueById(appendMaterials.current
					? [...materials, ...pageResults(response)]
					: pageResults(response));
				setMaterials(results);
				setPagedMaterialsResponse(response);
				appendMaterials.current = false;
			}
		} catch (error: any) {
			// Let errors bubble up to the error handling mechanism
			throw error;
		}
	};

	const fetchLights = async () => {
		// Track this request with incremental ID to prevent race conditions
		const requestId = ++lightsRequestIdRef.current;

		try {
			const queryString = getQueryStringFromPageQuery(lightsQuery);
			const response = await getLights(`?${queryString}`);
			logger.debug("[LibraryProvider] fetchLights", { queryString, requestId });

			// Only process if this is still the latest request
			if (requestId !== lightsRequestIdRef.current) {
				console.log(`[LibraryProvider] Ignoring stale lights request #${requestId} (current: #${lightsRequestIdRef.current})`);
				return;
			}

			if (responseLights.ok) {
				const results = uniqueById(appendLights.current
					? [...lights, ...pageResults(response)]
					: pageResults(response));
				setLights(results);
				setPagedLightsResponse(response);
				appendLights.current = false;
			}
		} catch (error: any) {
			// Let errors bubble up to the error handling mechanism
			throw error;
		}
	};

	const fetchCombinationsData = async () => {
		const requestId = ++combinationsRequestIdRef.current;

		try {
			const queryString = getQueryStringFromPageQuery(combinationsQuery);
			const response = await getCombinations(`?${queryString}`);
			logger.debug("[LibraryProvider] fetchCombinationsData", { queryString, requestId });

			if (requestId !== combinationsRequestIdRef.current) {
				console.log(`[LibraryProvider] Ignoring stale combinations request #${requestId} (current: #${combinationsRequestIdRef.current})`);
				return;
			}

			if (responseCombinations.ok) {
				const results = uniqueById(appendCombinations.current
					? [...combinations, ...pageResults(response)]
					: pageResults(response));
				setCombinations(sortCombinationsByName(results, i18n.language));
				setPagedCombinationsResponse(response);
				appendCombinations.current = false;
			}
		} catch (error: any) {
			throw error;
		}
	};

	/*
	const fetchAllObjects = async () => {
		const queryString = getQueryStringFromPageQuery({
			...initialObjectsQuery,
			limit: 100000,
		});
		const response = await getAllObjects(`?${queryString}`);
		logger.debug("[LibraryProvider] fetchAllObjects", { queryString });

		if (responseAllObjects.ok) {
			// console.log("all objects=", response.results);
			setAllObjects(response.results);
		}
	};
	*/

	/**
	 * Every material this org may USE: all public ones plus the org's own
	 * private ones. This is the list the scene editor's material store is fed
	 * (SceneView -> R3FScene -> useMaterialStore), so a material missing here
	 * cannot be painted - useMaterialDrop reports "material not found in store"
	 * for it - no matter that the library tab offers it.
	 *
	 * initialMaterialsQuery is public-only on purpose (see above): a top-level
	 * public-or-private $or cannot be served by an index together with the sort.
	 * That is a concern for the paged, searched, sorted library query, not for
	 * this one - but rather than reintroduce the $or, ask twice, once per
	 * visibility, each of which an index serves, and merge. The org's private
	 * set is small; the public set is capped by the server at 10000.
	 */
	const fetchAllMaterials = async () => {
		const fetchVisibility = async (visibility: {
			isPublic: boolean;
			isPrivate: boolean;
		}): Promise<Material[] | null> => {
			const queryString = getQueryStringFromPageQuery({
				...initialMaterialsQuery,
				filters: { ...initialMaterialsQuery.filters, ...visibility },
				limit: 100000,
			});
			const response = await getAllMaterials(`?${queryString}`);
			return responseAllMaterials.ok ? pageResults(response) : null;
		};

		const publicMaterials = await fetchVisibility({ isPublic: true, isPrivate: false });
		const privateMaterials = await fetchVisibility({ isPublic: false, isPrivate: true });

		// One failed request must not wipe what the other delivered; both failing
		// leaves the previous list in place, as a single failed request used to.
		if (publicMaterials === null && privateMaterials === null) {
			return;
		}
		setAllMaterials(
			uniqueById([...(publicMaterials ?? []), ...(privateMaterials ?? [])]),
		);
	};

	const fetchAllLights = async () => {
		const queryString = getQueryStringFromPageQuery({
			...initialLightsQuery,
			limit: 1000,
		});
		const response = await getAllLights(`?${queryString}`);
		// logger.debug("[LibraryProvider] fetchAllLights", { queryString });

		if (responseAllLights.ok) {
			setAllLights(pageResults(response));
		}
	};

	const loadMoreObjects = () => {
		if (!pagedObjectsResponse?.hasNextPage) {
			return;
		}

		appendObjects.current = true;
		setObjectsQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));

		// console.log("loadMoreObjects", objectsQuery);
	};

	const loadMoreGroupedObjects = () => {
		if (!pagedGroupedObjectsResponse?.hasNextPage) {
			return;
		}

		appendGroupedObjects.current = true;
		setObjectsQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	const loadMoreMaterials = () => {
		if (!pagedMaterialsResponse?.hasNextPage) {
			return;
		}

		appendMaterials.current = true;
		setMaterialsQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	const loadMoreLights = () => {
		if (!pagedLightsResponse?.hasNextPage) {
			return;
		}

		appendLights.current = true;
		setLightsQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	const loadMoreCombinations = () => {
		if (!pagedCombinationsResponse?.hasNextPage) {
			return;
		}

		appendCombinations.current = true;
		setCombinationsQuery((previousQuery) => ({
			...previousQuery,
			page: previousQuery.page + 1,
		}));
	};

	const setCurrentObject = async (idOrNull?: string | null) => {
		let targetObject;

		targetObject = objects?.find(
			(o) => o.id === idOrNull || o.shortid === idOrNull,
		) as SceneObject;

		if (!targetObject && idOrNull && !errorObjects) {
			// Determine endpoint based on ID type
			// MongoDB ObjectIds are 24 hex characters
			const isMongoId = /^[0-9a-fA-F]{24}$/.test(idOrNull);
			const endpoint = isMongoId ? `/${idOrNull}` : `/${idOrNull}/shortid`;

			// console.log(`[LibraryProvider] Fetching object by ${isMongoId ? 'MongoDB _id' : 'shortid'}: ${idOrNull}`);
			const result = await getObjects(endpoint);

			if (responseObjects.ok) {
				targetObject = result;
			}
		}

		if (targetObject && isLibraryView) {
			history.replace(`/library/objects/${targetObject.shortid}`, search);
		}

		setCurrentObjectState(targetObject);
	};

	const resetCurrentObject = async () => {
		if (!shortidParam) {
			console.error('[LibraryProvider] Cannot reset current object: shortidParam is undefined');
			setCurrentObjectState(undefined);
			return;
		}

		// console.log(`[LibraryProvider] Resetting current object by shortid: ${shortidParam}`);
		const result = await getObjects(`/${shortidParam}/shortid`);

		setCurrentObjectState(result);
	};

	const setCurrentMaterial = async (idOrNull?: string | null) => {
		let targetMaterial;

		targetMaterial = materials?.find(
			({ id, shortid }) => id === idOrNull || shortid === idOrNull,
		) as Material;

		if (!targetMaterial && idOrNull && !errorMaterials) {
			// Determine endpoint based on ID type
			// MongoDB ObjectIds are 24 hex characters
			const isMongoId = /^[0-9a-fA-F]{24}$/.test(idOrNull);
			const endpoint = isMongoId ? `/${idOrNull}` : `/${idOrNull}/shortid`;

			// console.log(`[LibraryProvider] Fetching material by ${isMongoId ? 'MongoDB _id' : 'shortid'}: ${idOrNull}`);
			const result = await getMaterials(endpoint);

			if (responseMaterials.ok) {
				targetMaterial = result;
			}
		}

		if (targetMaterial && isLibraryView) {
			history.replace(
				`/library/materials/${targetMaterial.shortid}`,
				search,
			);
		}

		setCurrentMaterialState(targetMaterial);
	};

	const setCurrentLight = async (idOrNull?: string | null) => {
		let targetLight;

		// _id as well as id: the list endpoint returns lean documents, which
		// carry no virtual `id`, and the gallery hands over _id.
		targetLight = lights?.find(
			({ _id, id, shortid }) =>
				_id === idOrNull || id === idOrNull || shortid === idOrNull,
		) as Light;

		if (!targetLight && idOrNull && !errorLights) {
			// Determine endpoint based on ID type
			// MongoDB ObjectIds are 24 hex characters
			const isMongoId = /^[0-9a-fA-F]{24}$/.test(idOrNull);
			const endpoint = isMongoId ? `/${idOrNull}` : `/${idOrNull}/shortid`;

			// console.log(`[LibraryProvider] Fetching light by ${isMongoId ? 'MongoDB _id' : 'shortid'}: ${idOrNull}`);
			const result = await getLights(endpoint);

			if (responseLights.ok) {
				targetLight = result;
			}
		}

		if (targetLight && isLibraryView) {
			history.replace(
				`/library/lights/${targetLight.shortid}`,
				search,
			);
		}

		setCurrentLightState(targetLight);
	};

	const setCurrentCombination = async (idOrNull?: string | null) => {
		let targetCombination;

		targetCombination = combinations?.find(
			({ _id, shortid }) => _id === idOrNull || shortid === idOrNull,
		) as Combination;

		// The 3D preview needs parts[] to render anything. A list entry that
		// carries none is refetched in full rather than shown empty — what the
		// paginated list returns is not guaranteed to include them.
		const needsFullDocument =
			!!targetCombination && !targetCombination.parts?.length;

		if ((!targetCombination || needsFullDocument) && idOrNull && !errorCombinations) {
			// Determine endpoint based on ID type
			// MongoDB ObjectIds are 24 hex characters
			const id = targetCombination?._id ?? idOrNull;
			const isMongoId = /^[0-9a-fA-F]{24}$/.test(id);
			const endpoint = isMongoId ? `/${id}` : `/${id}/shortid`;

			const result = await getCombinations(endpoint);

			// On failure the list entry stands: worse than the full document,
			// but better than dropping a selection the user just made.
			if (responseCombinations.ok && result) {
				targetCombination = result;
			}
		}

		if (targetCombination && isLibraryView) {
			history.replace(
				`/library/combinations/${targetCombination.shortid}`,
				search,
			);
		}

		setCurrentCombinationState(targetCombination);
	};

	/* Patches the selected combination in place. setCurrentCombination above
	   takes an ID and refetches; a panel that has just saved an edit already
	   knows the new values and would only be clearing its own selection by
	   handing that setter a record.

	   The fetched LIST is patched alongside the selection, and that is not
	   optional: setCurrentCombination resolves an id against `combinations`
	   first and only falls back to the API when the entry it finds has no
	   parts. Leaving the list alone therefore made every edit look like it had
	   been lost the moment the user clicked another combination and came back —
	   the save had gone through, but the stale list entry was what got
	   selected. */
	/**
	 * Object twin of updateCurrentCombination. Patches every list the object
	 * appears in, not just the selection: the gallery renders from `objects`,
	 * and `allObjectsCache` outlives the current search, so leaving either
	 * behind would put a stale copy back on screen at the next filter change.
	 */
	const updateCurrentObject = (patch: Partial<SceneObject>) => {
		const previous = currentObjectState;
		if (!previous) return;

		const updated = { ...previous, ...patch } as SceneObject;
		const replace = (list: SceneObject[]) =>
			list.map((object) =>
				object._id === updated._id || object.shortid === updated.shortid
					? updated
					: object,
			);

		setCurrentObjectState(updated);
		setObjects(replace);
		setAllObjectsCache(replace);
	};

	const updateCurrentLight = (patch: Partial<Light>) => {
		const previous = currentLightState;
		if (!previous) return;

		const updated = { ...previous, ...patch } as Light;

		setCurrentLightState(updated);
		setLights((list) =>
			list.map((light) => (light._id === updated._id ? updated : light)),
		);
	};

	const updateCurrentCombination = (patch: Partial<Combination>) => {
		const previous = currentCombinationState;
		if (!previous) return;

		const updated = { ...previous, ...patch } as Combination;

		setCurrentCombinationState(updated);
		/* Re-sorted, not just replaced: renaming a combination is the one edit
		   that changes where it belongs in the gallery, and it is made from the
		   Details panel right next to the tile that would otherwise stay put. */
		setCombinations((list) =>
			sortCombinationsByName(
				list.map((combination) =>
					combination._id === updated._id ||
					combination.shortid === updated.shortid
						? updated
						: combination,
				),
				i18n.language,
			),
		);
	};

	/* For deep linking, if library page is loaded with shortid into params. */
	useEffect(() => {
		if (
			currentOrganization &&
			objects &&
			shortidParam &&
			kindParam === "objects"
		) {
			setCurrentObject(shortidParam);
		}

		if (
			currentOrganization &&
			materials &&
			shortidParam &&
			kindParam === "materials"
		) {
			setCurrentMaterial(shortidParam);
		}

		if (
			currentOrganization &&
			lights &&
			shortidParam &&
			kindParam === "lights"
		) {
			setCurrentLight(shortidParam);
		}

		if (
			currentOrganization &&
			combinations &&
			shortidParam &&
			kindParam === "combinations"
		) {
			setCurrentCombination(shortidParam);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [objects, materials, lights, combinations, shortidParam, currentOrganization, kindParam]);

	useEffect(() => {
		// console.log('[LibraryProvider] objectsQuery changed:', objectsQuery);
		// Don't fetch objects on project scenes gallery page (only scene thumbnails shown)
		// b2c accounts have no object library — the server returns an empty list
		// for them, so the request would only cost a roundtrip. The editor gets
		// what it needs about the objects in a scene from objects/resolve.
		if (currentOrganization && !isProjectScenesGallery && !isB2c) {
			if (objectsBrowseLevel) {
				// Cards instead of results: nothing to fetch, and whatever an
				// earlier query left behind must not show up under them.
				++objectsRequestIdRef.current; // discards a request still in flight
				appendGroupedObjects.current = false;
				setGroupedObjects([]);
				setObjects([]);
				setObjectsCount(-1); // no "0 results" above the cards
				setPagedGroupedObjectsResponse(undefined);
				setLoadingGroupedObjects(false);
				return;
			}
			// Only fetch grouped objects now - fetchObjects() is deprecated
			// in favor of grouped endpoint for library view
			// console.log('[LibraryProvider] Calling fetchGroupedObjects');
			fetchGroupedObjects();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [objectsQuery, currentOrganization, isProjectScenesGallery, isB2c]);

	// Reset objectsQuery when organization changes to apply organization-specific defaults
	useEffect(() => {
		// Only reset query if not on project scenes gallery (where objects aren't needed)
		if (currentOrganization?._id && !isProjectScenesGallery) {
			setObjectsQuery(getInitialObjectsQuery(currentOrganization._id));
			setCombinationClassificationFilter(
				getInitialCombinationClassificationFilter(currentOrganization._id),
			);
			// Reset initial load flag when organization changes
			hasPerformedInitialLoad.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganization?._id, isProjectScenesGallery]);

	useEffect(() => {
		if (currentOrganization) {
			/// fetchAllObjects();
			fetchAllMaterials();
			// b2c accounts place no light sources; the server returns none.
			if (!isB2c) {
				fetchAllLights();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganization, isB2c]);

	useEffect(() => {
		if (currentOrganization) {
			if (materialsBrowseLevel) {
				// Cards instead of results, as for objects above — or the color
				// palette for the colors category. The editor's materials come
				// from allMaterials and are not affected.
				++materialsRequestIdRef.current; // discards a request still in flight
				appendMaterials.current = false;
				setMaterials([]);
				setPagedMaterialsResponse(undefined);
				return;
			}
			fetchMaterials();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [materialsQuery, currentOrganization]);

	useEffect(() => {
		if (currentOrganization && !isB2c) {
			fetchLights();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lightsQuery, currentOrganization, isB2c]);

	useEffect(() => {
		if (currentOrganization) {
			fetchCombinationsData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [combinationsQuery, currentOrganization]);

	/* The order follows the DISPLAYED name, so switching the UI language
	   reorders the gallery. Re-sorting the list we already have rather than
	   refetching it: the server sorts on meta.nameEN and would hand back the
	   same order it just did. */
	useEffect(() => {
		setCombinations((list) => sortCombinationsByName(list, i18n.language));
	}, [i18n.language]);


	useEffect(() => {
		if (currentObjectState) {
			setCurrentMaterialState(undefined);
			// TODO add lights here and insert third useEffect
		}
	}, [currentObjectState]);

	useEffect(() => {
		if (currentMaterialState) {
			setCurrentObjectState(undefined);
			// TODO add lights here and insert third useEffect
		}
	}, [currentMaterialState]);


	useEffect(() => {
		if (errorObjects || errorMaterials || errorLights || errorCombinations) {
			logger.debug(
				"[LibraryProvider] Critical error w/ objects/materials/lights/combinations",
				errorObjects,
				errorMaterials,
				errorLights,
				errorCombinations,
			);

			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("msg.general.error"),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorObjects, errorMaterials, errorLights, errorCombinations]);

	return (
		<Context.Provider
			value={{
				isB2c,
				objects,
				objectsQuery,
				pagedObjects: pagedObjectsResponse,
				objectsCount,
				currentObject: currentObjectState,
				loadingObjects,
				errorObjects,
				setCurrentObject,
				resetCurrentObject,
				setObjectsQuery,
				loadMoreObjects,
				loadSingleObject,

				allObjectsCache,

				groupedObjects,
				pagedGroupedObjects: pagedGroupedObjectsResponse,
				loadingGroupedObjects,
				loadMoreGroupedObjects,
				objectsBrowseLevel,

				materials,
				materialsBrowseLevel,
				materialsQuery,
				pagedMaterials: pagedMaterialsResponse,
				currentMaterial: currentMaterialState,
				loadingMaterials,
				errorMaterials,
				setCurrentMaterial,
				setMaterialsQuery,
				loadMoreMaterials,

				lights,
				lightsQuery,
				pagedLights: pagedLightsResponse,
				currentLight: currentLightState,
				loadingLights,
				errorLights,
				setCurrentLight,
				setLightsQuery,
				loadMoreLights,

				// allObjects,
				allMaterials,
				allLights,
				// fetchAllObjects,
				fetchAllMaterials,
				fetchAllLights,

				combinations,
				combinationsQuery,
				combinationClassificationFilter,
				setCombinationClassificationFilter,
				combinationVisibility,
				setCombinationVisibility,
				pagedCombinations: pagedCombinationsResponse,
				currentCombination: currentCombinationState,
				loadingCombinations,
				errorCombinations,
				setCurrentCombination,
				updateCurrentCombination,
				updateCurrentLight,
				updateCurrentObject,
				setCombinationsQuery,
				loadMoreCombinations,
			}}
		>
			{children}
		</Context.Provider>
	);
});

export default LibraryProvider;
